import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ZoningCodeService } from 'src/app/core/services/zoning-code.service';
import { IBaseTable, ITableSection } from 'src/app/interfaces/calibration-set-table.interface';
import { IZoningCodeModel } from 'src/app/interfaces/zoning-code-model.interface';
import { TableType } from 'src/shared/enums/table-type.enum';
import { LoaderService } from 'src/shared/services/loader.service';
import * as _ from 'lodash';
import { IBaseTableCell } from 'src/app/interfaces/tables/cells/base-table-cell-interface';
import { FieldType } from 'src/shared/enums/field-type.enum';
import { ViewSettingsService } from 'src/app/core/services/view-settings.service';
import { IFieldSettings } from 'src/app/interfaces/field-settings.interface';
import { AccordionComponent } from 'src/app/draft-page/accordion/accordion.component';
import { CalibrationSetType, getCalibrationSetTypeId } from 'src/shared/enums';

@Component({
    standalone: false,
    selector: 'app-table-with-sections',
    templateUrl: './table-with-sections.component.html',
    styleUrls: ['./table-with-sections.component.scss']

})
export class TableWithSectionsComponent implements OnInit, OnDestroy {
    @Input()
    currentTab: string;

    @Input()
    type: TableType;

    @Input()
    showRequiredFieldsSwitch: IBaseTable;

    @Input()
    hideAttributeButtons: boolean = false;

    @Input()
    readOnly: boolean = false;

    @Input()
    uploadZoningCode: boolean = true;

    @Input()
    validateHeaderFields: boolean = false;

    @Input()
    set zoningCode(value) {
        this.initZoningCode(value);
    }

    get zoningCode() {
        return this._zoningCode;
    }

    @ViewChild(AccordionComponent)
    accordion: AccordionComponent;

    fieldType: any = FieldType;

    table: IBaseTable;
    private _zoningCode: IZoningCodeModel;
    private subscriptions: Subscription[] = [];
    hasSections: boolean = false;
    allSections: ITableSection[] = [];
    sections: ITableSection[] = [];
    calibrations: IBaseTableCell<any>[] = [];
    calibrationSetType: CalibrationSetType;

    constructor(
        private readonly zcService: ZoningCodeService,
        private loaderService: LoaderService,
        protected readonly viewSettingsService: ViewSettingsService
    ) {}

    ngOnInit() {
        if (this.uploadZoningCode) {
            this.loaderService.show();
            this.subscriptions.push(
                this.zcService.zoningCodes.pipe(filter(zc => zc !== null && zc.length > 0)).subscribe(zc => {
                    this.initZoningCode(zc[0]);
                })
            );
        }
        this.subscriptions.push(
            this.viewSettingsService.showOnlyRequiredFields$.subscribe(() => {
                this.sections = this.allSections.filter(section => this.hasVisibleFields(section));
                if (this.accordion && this.accordion.initToggleSubscriptions) {
                    setTimeout(() => {
                        this.accordion.initToggleSubscriptions();
                    }, 300);
                }
            })
        );
        this.calibrationSetType = getCalibrationSetTypeId(this.currentTab);
    }

    initZoningCode(zoningCode: IZoningCodeModel): void {
        this._zoningCode = zoningCode;
        if (!this._zoningCode) {
            return;
        }

        this.table = this._zoningCode.initCalibrationSetTable(this.type, this.currentTab);
        this.allSections = _.get(this, 'table.settings.sections', []);
        this.sections = this.allSections.filter(section => this.hasVisibleFields(section));
        this.calibrations = _.get(this, 'table.table', []);
        this.hasSections = _.get(this, 'table.settings.hasSections', false);
        this.loaderService.hide();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => !subscription.closed && subscription.unsubscribe());
    }

    getCellIdentifier(columnIndex, index) {
        return `${columnIndex}-${index}`;
    }

    getSectionFields(section: ITableSection): IFieldSettings[] {
        return this.showRequiredFieldsSwitch && this.viewSettingsService.showOnlyRequiredFields
            ? section.requiredFields
            : section.fields;
    }

    hasVisibleFields(section: ITableSection): boolean {
        return !!this.getSectionFields(section).length;
    }

    fieldHasValidationIssues(field: IFieldSettings): boolean {
        if (!this.validateHeaderFields) {
            return false;
        }
        return this.zoningCode?.importValidationResult?.zoneCombinations?.fieldsWithIssues?.includes(field.name);
    }
}
